import QueryString from "query-string";
import {
  all,
  call,
  debounce,
  put,
  select,
  takeEvery,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import { getFeedStats } from "../Feed/actions";
import {
  addReactionFailure,
  addReactionSuccess,
  deleteTagFailure,
  deleteTagSuccess,
  fetchReactionsFailure,
  fetchReactionsSuccess,
  fetchSeenUsersFailure,
  fetchSeenUsersSuccess,
  fetchEventRsvpListFailure,
  fetchEventRsvpListSuccess,
  searchTagFailure,
  searchTagSuccess,
  acknowledgeFeedFailure,
  acknowledgeFeedSuccess,
  recordFeedViewFailure,
  recordFeedViewSuccess,
  fetchStatsSuccess,
  fetchStatsFailure,
  fetchEngageReportSuccess,
  fetchEngageReportFailure,
  getKudosLeaderboardSuccess,
  getKudosLeaderboardFailure,
  fetchKudosReportSuccess,
  fetchKudosReportFailure,
  fetchValuesReportSuccess,
  fetchValuesReportFailure,
  fetchCompanyScorecardReportSuccess,
  fetchCompanyScorecardReportFailure,
  exportAcknowledgedFeedsSuccess,
  exportAcknowledgedFeedsFailure,
  pinFeedSuccess,
  pinFeedFailure,
} from "./actions";
import {
  ADD_REACTION,
  DELETE_TAG,
  FETCH_REACTIONS,
  FETCH_SEEN_USERS,
  FETCH_EVENT_RSVP_LIST,
  SEARCH_TAG,
  ACKNOWLEDGE_FEED,
  RECORD_FEED_VIEW,
  RECORD_FEED_VIEW_SUCCESS,
  FETCH_STATS,
  FETCH_ENGAGE_REPORT,
  GET_KUDOS_LEADERBOARD,
  FETCH_KUDOS_REPORT,
  FETCH_VALUES_REPORT,
  FETCH_COMPANY_SCORECARD_REPORT,
  EXPORT_ACKNOWLEDGED_FEEDS,
  PIN_FEED,
} from "./constants";

export function* searchTagsSaga({ payload, onSuccess, values }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload?.filters ? QueryString.stringify(payload?.filters) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/search-feed-tag?${query}`,
    );
    yield put(searchTagSuccess(data));
    typeof onSuccess === "function" && onSuccess(data, values);
  } catch (e) {
    yield put(searchTagFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching employee!"));
  }
}

export function* deleteTagSaga({ payload, onDelete }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const data = yield call(api(token).delete, `/api/feed-tag/${payload}`);
    if (data) {
      yield put(successAlert("Tag removed successfully!"));
      yield put(deleteTagSuccess());
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteTagFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* addReactionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/reaction-create-delete`,
      payload,
    );
    if (data) {
      yield put(addReactionSuccess(data));
      typeof onSuccess === "function" && onSuccess(data);
    }
  } catch (e) {
    yield put(addReactionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* fetchReactionsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/feed-reaction-type-list/${payload}`,
    );
    yield put(fetchReactionsSuccess(data));
  } catch (e) {
    yield put(fetchReactionsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* acknowledgeFeedSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/feed-acknowledgement/add`,
      payload,
    );

    if (data) {
      yield put(acknowledgeFeedSuccess(data));
      typeof onSuccess === "function" && onSuccess(data);
    }
  } catch (e) {
    yield put(acknowledgeFeedFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while acknowledging feed!"));
  }
}

export function* fetchSeenUsersSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/feed/${payload}/seen-users`,
    );
    yield put(fetchSeenUsersSuccess(data));
  } catch (e) {
    yield put(fetchSeenUsersFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

function normalizeEventRsvpListPayload(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rsvps)) return data.rsvps;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

export function* fetchEventRsvpListSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/feed/${payload}/event-rsvp`,
    );
    yield put(fetchEventRsvpListSuccess(normalizeEventRsvpListPayload(data)));
  } catch (e) {
    yield put(fetchEventRsvpListFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* recordFeedViewSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const feedId = typeof payload === "object" ? payload?.feedId : payload;

  if (!feedId) {
    yield put(recordFeedViewFailure());
    return;
  }

  try {
    const { data } = yield call(api(token).post, `/api/feed/${feedId}/view`);
    yield put(
      recordFeedViewSuccess({
        feedId,
        data: data ?? {},
      }),
    );
  } catch (e) {
    yield put(
      recordFeedViewFailure({
        feedId,
      }),
    );
  }
}

export function* fetchStatsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/feed-acknowledgement-stats/${payload}`,
    );
    yield put(fetchStatsSuccess(data));
  } catch (e) {
    yield put(fetchStatsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* fetchEngageReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";

  try {
    const { data: body } = yield call(
      api(token).get,
      `/api/engagement-report?${query}`,
    );
    const inner = body?.data ?? body;
    yield put(fetchEngageReportSuccess(inner));
  } catch (e) {
    yield put(fetchEngageReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* getKudosLeaderboardSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  // remove empty values from payload
  const filteredPayload = Object.fromEntries(
    Object.entries(payload).filter(
      ([_, value]) => value !== null && value !== undefined,
    ),
  );
  const query = filteredPayload ? QueryString.stringify(filteredPayload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/kudos-leaderboard?${query}`,
    );
    yield put(getKudosLeaderboardSuccess(data));
  } catch (e) {
    yield put(getKudosLeaderboardFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* fetchKudosReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const filteredPayload = Object.fromEntries(
    Object.entries(payload || {}).filter(
      ([_, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
  const query = QueryString.stringify(filteredPayload);
  try {
    const { data: body } = yield call(
      api(token).get,
      `/api/kudos-report?${query}`,
    );
    const inner = body?.data ?? body;
    yield put(fetchKudosReportSuccess(inner));
  } catch (e) {
    yield put(fetchKudosReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* fetchValuesReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const filteredPayload = Object.fromEntries(
    Object.entries(payload || {}).filter(
      ([_, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
  const query = QueryString.stringify(filteredPayload);
  try {
    const { data: body } = yield call(
      api(token).get,
      `/api/values-report?${query}`,
    );
    const inner = body?.data ?? body;
    yield put(fetchValuesReportSuccess(inner));
  } catch (e) {
    yield put(fetchValuesReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* fetchCompanyScorecardReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const filteredPayload = Object.fromEntries(
    Object.entries(payload || {}).filter(
      ([_, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
  const query = QueryString.stringify(filteredPayload);
  try {
    const { data: body } = yield call(
      api(token).get,
      `/api/company-scorecard-report?${query}`,
    );
    const inner = body?.data ?? body;
    yield put(fetchCompanyScorecardReportSuccess(inner));
  } catch (e) {
    yield put(fetchCompanyScorecardReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* exportAcknowledgedFeedsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { title, id } = payload;
  try {
    const data = yield call(
      api(token, null, false, true).get,
      `/api/feed-acknowledgement/export/${id}`,
    );
    if (data) {
      yield put(exportAcknowledgedFeedsSuccess());
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title}.csv`);
      document.body.appendChild(link);
      link.click();
    }
  } catch (e) {
    yield put(exportAcknowledgedFeedsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(
        errorAlert(
          "There are no acknowledgement records available for this feed.",
        ),
      );
  }
}

export function* pinFeedSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, type } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/feed/${id}/${type}`,
      payload,
    );
    if (data) {
      yield put(pinFeedSuccess());
      typeof onSuccess === "function" && onSuccess(data);
      yield put(successAlert(`Feed has been ${type}ed!`));
    }
  } catch (e) {
    yield put(pinFeedFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* searchTagsFlow() {
  yield takeEvery(SEARCH_TAG, searchTagsSaga);
}

export function* deleteTagFlow() {
  yield takeEvery(DELETE_TAG, deleteTagSaga);
}

export function* addReactionFlow() {
  yield takeEvery(ADD_REACTION, addReactionSaga);
}

export function* fetchReactionsFlow() {
  yield takeEvery(FETCH_REACTIONS, fetchReactionsSaga);
}

export function* fetchSeenUsersFlow() {
  yield takeEvery(FETCH_SEEN_USERS, fetchSeenUsersSaga);
}

export function* fetchEventRsvpListFlow() {
  yield takeEvery(FETCH_EVENT_RSVP_LIST, fetchEventRsvpListSaga);
}

export function* acknowledgeFeedFlow() {
  yield takeEvery(ACKNOWLEDGE_FEED, acknowledgeFeedSaga);
}

export function* fetchStatsFlow() {
  yield takeEvery(FETCH_STATS, fetchStatsSaga);
}

export function* recordFeedViewFlow() {
  yield takeEvery(RECORD_FEED_VIEW, recordFeedViewSaga);
}

export function* fetchEngageReportFlow() {
  yield takeEvery(FETCH_ENGAGE_REPORT, fetchEngageReportSaga);
}

export function* getKudosLeaderboardFlow() {
  yield takeEvery(GET_KUDOS_LEADERBOARD, getKudosLeaderboardSaga);
}

export function* fetchKudosReportFlow() {
  yield takeEvery(FETCH_KUDOS_REPORT, fetchKudosReportSaga);
}

export function* fetchValuesReportFlow() {
  yield takeEvery(FETCH_VALUES_REPORT, fetchValuesReportSaga);
}

export function* fetchCompanyScorecardReportFlow() {
  yield takeEvery(
    FETCH_COMPANY_SCORECARD_REPORT,
    fetchCompanyScorecardReportSaga,
  );
}

export function* exportAcknowledgedFeedsFlow() {
  yield takeEvery(EXPORT_ACKNOWLEDGED_FEEDS, exportAcknowledgedFeedsSaga);
}

export function* pinFeedFlow() {
  yield takeEvery(PIN_FEED, pinFeedSaga);
}

export function* refreshFeedCountsAfterViewSaga() {
  yield put(getFeedStats());
}

export function* refreshFeedCountsAfterViewFlow() {
  // Debounce: the user may scroll past many feeds triggering multiple view
  // successes. Wait 1.5 s after the last success before calling the counts API
  // so that exactly one refresh happens per "scroll burst".
  yield debounce(
    1500,
    RECORD_FEED_VIEW_SUCCESS,
    refreshFeedCountsAfterViewSaga,
  );
}

export default function* FeedActionSaga() {
  yield all([
    searchTagsFlow(),
    deleteTagFlow(),
    addReactionFlow(),
    fetchReactionsFlow(),
    fetchSeenUsersFlow(),
    fetchEventRsvpListFlow(),
    acknowledgeFeedFlow(),
    recordFeedViewFlow(),
    fetchStatsFlow(),
    fetchEngageReportFlow(),
    getKudosLeaderboardFlow(),
    fetchKudosReportFlow(),
    fetchValuesReportFlow(),
    fetchCompanyScorecardReportFlow(),
    exportAcknowledgedFeedsFlow(),
    pinFeedFlow(),
    refreshFeedCountsAfterViewFlow(),
  ]);
}
